import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, DollarSign, Scale, CheckCircle, AlertCircle, TrendingUp, Shield, ChevronRight, Mail, Calculator } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PageVideoSection from '@/components/PageVideoSection';

// Pricing/contingency fee video
const pricingVideos = [
  {
    id: '6FzbTsTiZew',
    title: "No Win, No Fee: How Contingency Fees Work",
    description: "Understanding how you pay for a personal injury attorney—and why there's no risk to you.",
    duration: "1:32"
  }
];

const PricingPage = () => {
  // FAQ data for pricing page schema
  const pricingFAQs = [
    {
      question: "How much does a personal injury attorney cost?",
      answer: "I work on a contingency fee basis, meaning you pay nothing upfront. My fee is a percentage (typically 33-40%) of your settlement or verdict. If we don't win, you owe me nothing."
    },
    {
      question: "What is a contingency fee?",
      answer: "A contingency fee means the attorney's payment is contingent on winning your case. Instead of hourly billing, the attorney takes a percentage of your recovery. This allows injured people to hire quality legal representation without financial risk."
    },
    {
      question: "Are there any upfront costs for a personal injury case?",
      answer: "No. I advance all case costs (filing fees, expert witnesses, medical records, etc.) and only get reimbursed if we win your case. You never pay out of pocket."
    },
    {
      question: "What percentage does a personal injury attorney take?",
      answer: "Most contingency fees range from 33-40% depending on case complexity and whether the case settles or goes to trial. This percentage is discussed upfront during your free consultation."
    },
    {
      question: "Is the initial consultation free?",
      answer: "Yes, I offer a completely free, no-obligation consultation. I'll review your case, explain your options, and give you an honest assessment—even if I'm not the right attorney for your situation."
    }
  ];

  // HowTo schema for understanding contingency fees
  const howToSteps = {
    name: "How Contingency Fee Pricing Works for Personal Injury Cases",
    description: "Understanding how you pay for a personal injury attorney and what to expect with contingency fee arrangements.",
    totalTime: "PT5M",
    steps: [
      { name: "No Upfront Costs", text: "You pay nothing to get started. No retainer fees, no hourly billing. I cover all case costs upfront." },
      { name: "Free Case Evaluation", text: "I review your case at no charge to determine if you have a viable claim and what it might be worth." },
      { name: "I Work on Your Case", text: "I handle all negotiations, paperwork, and legal strategy. You focus on healing while I fight for your compensation." },
      { name: "Fee Only If You Win", text: "My fee is a percentage (typically 33-40%) of your settlement or verdict. If we don't win, you owe me nothing." },
      { name: "You Receive Your Compensation", text: "After the case settles, legal fees and any case costs are deducted, and you receive the remainder of your recovery." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="pricing-page">
      <SEOHead 
        title="Pricing & Costs | Contingency Fee Attorney | Amy C. Brown Law"
        description="No upfront costs. No fees unless you win. Transparent contingency fee pricing for personal injury cases. Free consultation. Learn exactly what to expect."
        howToData={howToSteps}
        faqData={pricingFAQs}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              Transparent Pricing
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              How Much Does Hiring a Lawyer Cost?
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              I know money is one of the first things on your mind after an injury — medical bills, lost wages, and uncertainty add up fast. Let me be upfront about how I work.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="pricing-hero-call"
              >
                <Phone className="w-5 h-5" />
                (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contingency Fee Explanation */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Contingency Fee Basis
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                At Amy C. Brown Law, I handle all personal injury, car crash, motorcycle, slip and fall, first responder, and maritime cases on a <strong>contingency fee basis</strong>.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">You pay zero upfront</h4>
                    <p className="text-slate-600">No retainers, no hourly bills during the case.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">I only get paid if we win</h4>
                    <p className="text-slate-600">I only get paid if we win or settle your case successfully.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">Typical fee: 33–40% of recovery</h4>
                    <p className="text-slate-600">The percentage depends on case complexity, stage reached, and effort required — e.g., higher if it goes to trial.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Scale className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">Case expenses advanced by me</h4>
                    <p className="text-slate-600">Filing fees, experts, medical records, etc. are advanced by me and only reimbursed from the settlement if we win — they come out of the gross recovery first.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-3xl p-8 md:p-10">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-6">
                Why Contingency?
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                It aligns our interests perfectly: <strong>I succeed only if you do.</strong> It removes financial risk so you can focus on healing instead of worrying about lawyer bills.
              </p>
              
              <div className="bg-teal-600 rounded-2xl p-6 text-white mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="w-8 h-8 text-teal-200" />
                  <span className="text-3xl font-bold">3–5×</span>
                </div>
                <p className="text-teal-100">
                  Studies show represented claimants often recover 3–5 times more than those going alone, even after fees.
                </p>
              </div>

              <p className="text-slate-600 leading-relaxed">
                During your free consultation, I'll give you a straight, realistic estimate based on your facts — no pressure, no obligation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compensation Estimator Link */}
      <section className="py-16 bg-accent-soft">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-teal-500">
            <div className="flex items-start gap-4">
              <Calculator className="w-8 h-8 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading text-xl font-semibold text-secondary mb-3">
                  Wondering What Your Case Might Be Worth?
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Try my free Potential Compensation Estimator to get a rough idea of your case value. Answer six simple questions and get an educational estimate — no contact information required.
                </p>
                <Link 
                  to="/compensation-estimator"
                  className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-all"
                  data-testid="pricing-estimator-link"
                >
                  <Calculator className="w-5 h-5" />
                  Try the Estimator
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-16 bg-accent-soft">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-teal-500">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading text-xl font-semibold text-secondary mb-3">
                  Important Disclaimer
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  This is general information — every case is unique. The information on this page is not personalized legal advice. Contact me directly so we can discuss your specific situation honestly.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="tel:425-675-1224"
                    className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700"
                  >
                    <Phone className="w-4 h-4" />
                    (425) 675-1224
                  </a>
                  <a 
                    href="mailto:amy@amycbrownlaw.com"
                    className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700"
                  >
                    <Mail className="w-4 h-4" />
                    amy@amycbrownlaw.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Understanding Your Options
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Making an informed decision about legal representation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Contingency Fee
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>No upfront cost</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Shared risk — I only get paid if you win</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>More accessible for injury clients</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Aligned interests</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border-2 border-dashed border-slate-200">
              <div className="w-14 h-14 bg-slate-200 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-slate-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Hourly Billing
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0">×</span>
                  <span>Can cost thousands regardless of outcome</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0">×</span>
                  <span>Risky if case drags or doesn't succeed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0">×</span>
                  <span>Financial burden during recovery</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0">×</span>
                  <span>Often not ideal for injury cases</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-slate-600 mt-8 max-w-2xl mx-auto">
            For most injury clients (especially with medical bills piling up), contingency is far more accessible and fair.
          </p>
        </div>
      </section>

      {/* Practice Areas Links */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-2xl font-bold text-secondary text-center mb-8">
            Practice Areas I Handle on Contingency
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/car-crashes" className="bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all flex items-center gap-2">
              Car Crashes <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/motorcycle-crashes" className="bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all flex items-center gap-2">
              Motorcycle Crashes <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/maritime-injuries" className="bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all flex items-center gap-2">
              Maritime Injuries <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/first-responder-crashes" className="bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all flex items-center gap-2">
              First Responder <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/slip-and-falls" className="bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all flex items-center gap-2">
              Slip and Falls <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Video Resources Section */}
      <PageVideoSection
        videos={pricingVideos}
        title="Understanding Fees"
        subtitle="Watch this quick video to understand how contingency fees protect you."
        theme="teal"
      />

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation - No Obligation
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Call, text, or email me directly to discuss your specific situation honestly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 shadow-lg"
              data-testid="pricing-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Text (425) 675-1224
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
